import {
  isMobileDataProductsResponse,
  isMobileProductsResponse,
  ProductAvailabilityFilter
} from '@belong/api/hooks/useMobileProducts';
import { logger } from '@belong/logging/logger';
import { ICatalogOffer, IMobileDataProductsResponse, IMobileProductsResponse, PRODUCT_FAMILY } from '@belong/types';
import { loadProductAndOffers } from './loadProductData.helpers';

export type ProductResponseTypes = IMobileProductsResponse | IMobileDataProductsResponse;

// Generic T is narrowable, but defaults to all possible response types
interface IProductAndOfferResponse<T = ProductResponseTypes> {
  products: T;
  offers: ICatalogOffer[];
}

// Tuple type for product load requests, where the first element is the product family and the second element is a boolean indicating whether to fetch offers
// If required, a third element can be included for filtering by product availability
export type IProductLoadRequest =
  | [PRODUCT_FAMILY.MOBILE_PLANS | PRODUCT_FAMILY.MOBILE_DATA_PLANS, boolean]
  | [PRODUCT_FAMILY.MOBILE_PLANS | PRODUCT_FAMILY.MOBILE_DATA_PLANS, boolean, ProductAvailabilityFilter];

export interface IProductData {
  [PRODUCT_FAMILY.MOBILE_PLANS]?: IProductAndOfferResponse<IMobileProductsResponse>;
  [PRODUCT_FAMILY.MOBILE_DATA_PLANS]?: IProductAndOfferResponse<IMobileDataProductsResponse>;
}

const isFulfilled = <T>(result: PromiseSettledResult<T>): result is PromiseFulfilledResult<T> => {
  return result.status === 'fulfilled';
};

/**
 * Fetch product data and offers for all MOBILE product families.
 * Note: Data loading is segregated by product family to avoid blocking the entire page if one product family fails to load.
 * 1. Load product data from the Product API
 * 2. Load offers from the Offer API if required (fetchOffers = true)
 * 3. Return product data and offers in a single object for each product family
 */
export async function loadProductData(
  resolvedUrl: string | undefined,
  ...requests: IProductLoadRequest[]
): Promise<IProductData> {
  // Use Promise.allSettled to handle each product family independently, allowing for partial success
  const results = await Promise.allSettled(
    requests.map(([productFamily, fetchOffers, filter]) =>
      loadProductAndOffers(productFamily, fetchOffers, filter, resolvedUrl)
    )
  );

  const products: IProductData = {};

  results.forEach(result => {
    if (!isFulfilled(result)) {
      logger.error('Failed to load product data and offers:', result.reason);
      return;
    }

    if (isMobileProductsResponse(result.value.products)) {
      products[PRODUCT_FAMILY.MOBILE_PLANS] = result.value as IProductAndOfferResponse<IMobileProductsResponse>;
    }

    if (isMobileDataProductsResponse(result.value.products)) {
      products[PRODUCT_FAMILY.MOBILE_DATA_PLANS] =
        result.value as IProductAndOfferResponse<IMobileDataProductsResponse>;
    }
  });

  return products;
}
